import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Facebook, Instagram, Link2, Linkedin, Plug, Trash2, AtSign } from "lucide-react";
import { isUnlimited, type Platform } from "@marketing/shared";
import { apiErrorMessage } from "@/lib/api";
import { useBrands, useCurrentWorkspace } from "@/lib/queries";
import {
  useAssignPages,
  useAvailablePages,
  useConnectedAccounts,
  useDisconnectAccount,
  useMovePageBrand,
  useStartMetaConnect,
  useStartThreadsConnect,
  type PageAssignment,
} from "@/lib/social";
import { Notice } from "@/components/ui";
import { ErrorBox } from "./LoginPage";

const PLATFORM_ICON: Partial<Record<Platform, typeof Facebook>> = {
  facebook: Facebook,
  instagram: Instagram,
  linkedin: Linkedin,
  threads: AtSign,
};

/**
 * Connect pages (spec §8).
 *
 * Two states in one screen, driven by the `connection` query param that the
 * OAuth callback redirects back with: without it, the entry point; with it,
 * the page picker for that login.
 */
export function ConnectPage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const connectionId = params.get("connection");
  const callbackError = params.get("error");

  const brands = useBrands();
  const accounts = useConnectedAccounts();
  const workspace = useCurrentWorkspace();
  const startMeta = useStartMetaConnect();
  const startThreads = useStartThreadsConnect();
  const pages = useAvailablePages(connectionId);
  const assign = useAssignPages(connectionId);
  const disconnect = useDisconnectAccount();
  const move = useMovePageBrand();

  /**
   * Begins an OAuth flow, and SHOWS what went wrong if it cannot.
   *
   * Both mutations navigate away on success and had no error handling at all,
   * so a server that refused — "Meta is not configured. Set META_APP_ID,
   * META_APP_SECRET and META_REDIRECT_URI." — produced a button that did
   * visibly nothing. The API had already written the exact reason; it was
   * being thrown away one layer above.
   */
  function startConnect(provider: "meta" | "threads") {
    if (!effectiveStartBrand) return;
    setError(null);
    const onError = (err: unknown) => setError(apiErrorMessage(err, t("errors.generic")));

    if (provider === "meta") {
      startMeta.mutate(effectiveStartBrand, { onError });
    } else {
      startThreads.mutate(effectiveStartBrand, { onError });
    }
  }

  /** externalId → { brandId, platforms } chosen in the picker. */
  const [picks, setPicks] = useState<Record<string, { brandId: string; platforms: Platform[] }>>({});
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  /**
   * The brand a page should be connected to.
   *
   * Only ever assumed when there is exactly one brand to assume. It used to
   * fall back to `brands.data[0]`, which is right until the day a second
   * brand exists and then silently attaches every page to the first one —
   * with no error, because the choice looked made.
   *
   * With several brands this is "" until the owner says, and the picker will
   * not submit without it.
   */
  const onlyBrandId = brands.data?.length === 1 ? brands.data[0]!.id : "";
  const defaultBrandId = params.get("brand") ?? onlyBrandId;

  /** Chosen before leaving for Facebook, so the callback carries it back. */
  const [startBrandId, setStartBrandId] = useState("");
  const effectiveStartBrand = startBrandId || onlyBrandId;

  // Pre-tick every page that isn't already connected, with Facebook selected
  // and Instagram too where it's available — the common intent is "connect
  // what I just authorised".
  useEffect(() => {
    if (!pages.data || !defaultBrandId) return;
    setPicks((current) => {
      if (Object.keys(current).length > 0) return current;
      const seeded: typeof current = {};
      for (const p of pages.data) {
        if (p.alreadyConnected) continue;
        seeded[p.externalId] = { brandId: defaultBrandId, platforms: [...p.platforms] };
      }
      return seeded;
    });
  }, [pages.data, defaultBrandId]);

  function togglePlatform(externalId: string, platform: Platform, available: Platform[]) {
    setPicks((current) => {
      const entry = current[externalId] ?? { brandId: defaultBrandId, platforms: [] };
      const has = entry.platforms.includes(platform);
      const platforms = has
        ? entry.platforms.filter((p) => p !== platform)
        : [...entry.platforms, platform].filter((p) => available.includes(p));
      const next = { ...current, [externalId]: { ...entry, platforms } };
      // Dropping every platform means "don't connect this page" — remove the
      // entry entirely so it isn't submitted with an empty platform list.
      if (platforms.length === 0) delete next[externalId];
      return next;
    });
  }

  async function submit() {
    setError(null);
    const assignments: PageAssignment[] = Object.entries(picks)
      .filter(([, v]) => v.platforms.length > 0 && v.brandId)
      .map(([externalId, v]) => ({ externalId, brandId: v.brandId, platforms: v.platforms }));

    if (assignments.length === 0) {
      setError(t("connect.selectOne"));
      return;
    }

    // A page ticked with no brand chosen would otherwise be filtered out by
    // the line above and connect silently without it.
    const unassigned = Object.entries(picks).filter(
      ([, v]) => v.platforms.length > 0 && !v.brandId,
    );
    if (unassigned.length > 0) {
      setError(t("connect.needBrandPerPage"));
      return;
    }
    try {
      await assign.mutateAsync(assignments);
      // Drop the single-use connection from the URL so a refresh doesn't
      // retry a consumed connection and show a confusing error.
      setParams(new URLSearchParams(), { replace: true });
      setPicks({});
    } catch (err) {
      setError(apiErrorMessage(err, t("errors.generic")));
    }
  }

  /**
   * Disconnecting can take unpublished drafts with it, so it asks first and
   * then says what it did. Published posts are never touched — the page row is
   * kept behind the scenes to hold their permalinks.
   */
  async function onDisconnect(accountId: string, name: string) {
    setError(null);
    setNotice(null);
    if (!window.confirm(t("connect.confirmDisconnect", { name }))) return;
    try {
      const result = await disconnect.mutateAsync(accountId);
      setNotice(
        result.draftsRemoved > 0
          ? t("connect.disconnectedWithDrafts", { name, count: result.draftsRemoved })
          : t("connect.disconnected", { name }),
      );
    } catch (err) {
      setError(apiErrorMessage(err));
    }
  }

  const noBrands = !brands.isLoading && (brands.data?.length ?? 0) === 0;

  // The plan counts destinations, not logins: one Facebook page with its
  // Instagram account is two. Mirrors assertPageLimit in the API, which stays
  // the authority — this only avoids sending a request that cannot succeed.
  const maxPages = workspace.data?.limits.maxPages ?? 1;
  // An unlimited plan's limit is MAX_SAFE_INTEGER, which must never be shown
  // to a person as a number.
  const limitLabel = isUnlimited(maxPages) ? t("common.unlimited") : String(maxPages);
  const connectedCount = (accounts.data ?? []).filter(
    (a) => a.status !== "disconnected",
  ).length;
  const selectedCount = Object.values(picks).reduce((n, v) => n + v.platforms.length, 0);
  const overLimit = selectedCount > 0 && connectedCount + selectedCount > maxPages;
  // A ticked page with no brand chosen cannot be connected anywhere.
  const missingBrand = Object.values(picks).some((v) => v.platforms.length > 0 && !v.brandId);

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-xl font-semibold tracking-tight text-ink">{t("connect.title")}</h1>
        <p className="mt-1 text-sm text-muted">{t("connect.subtitle")}</p>
      </header>

      {callbackError ? <ErrorBox>{callbackError}</ErrorBox> : null}

      {/* ── Picker: only while an unconsumed connection is in the URL ── */}
      {connectionId ? (
        <section className="card-pad">
          <h2 className="text-sm font-semibold text-ink">{t("connect.pickerTitle")}</h2>
          <p className="mt-1 text-xs text-muted">{t("connect.pickerHint")}</p>

          {pages.isLoading ? (
            <p className="mt-4 text-sm text-muted">{t("errors.loading")}</p>
          ) : pages.isError ? (
            <div className="mt-4">
              <ErrorBox>{(pages.error as Error).message}</ErrorBox>
            </div>
          ) : (pages.data?.length ?? 0) === 0 ? (
            <div className="mt-4 space-y-2">
              <p className="text-sm text-ink">{t("connect.noPages")}</p>
              <p className="text-sm text-muted">{t("connect.noPagesFix")}</p>
            </div>
          ) : (
            <>
              <ul className="mt-4 divide-y divide-border">
                {pages.data!.map((p) => {
                  const entry = picks[p.externalId];
                  return (
                    <li key={p.externalId} className="flex flex-wrap items-center gap-3 py-3">
                      <span className="flex min-w-0 flex-1 items-center gap-2">
                        {p.avatarUrl ? (
                          <img src={p.avatarUrl} alt="" className="h-7 w-7 rounded-full" />
                        ) : (
                          <Plug size={16} className="text-muted" />
                        )}
                        <span className="truncate text-sm font-medium">{p.name}</span>
                        {p.alreadyConnected ? (
                          <span className="rounded-full bg-success/10 px-2 py-0.5 text-[10px] font-semibold text-success">
                            {t("connect.alreadyConnected")}
                          </span>
                        ) : null}
                      </span>

                      <span className="flex items-center gap-2">
                        {p.platforms.map((platform) => {
                          const Icon = PLATFORM_ICON[platform] ?? Plug;
                          const on = entry?.platforms.includes(platform) ?? false;
                          return (
                            <button
                              key={platform}
                              type="button"
                              aria-pressed={on}
                              onClick={() => togglePlatform(p.externalId, platform, p.platforms)}
                              className={
                                "inline-flex items-center gap-1 rounded-lg border px-2 py-1 text-xs transition " +
                                (on
                                  ? "border-primary-500 bg-primary-50 text-primary-700"
                                  : "border-border text-muted hover:bg-surface")
                              }
                            >
                              <Icon size={13} /> {platform}
                            </button>
                          );
                        })}
                      </span>

                      <select
                        value={entry?.brandId ?? defaultBrandId}
                        onChange={(e) =>
                          setPicks((c) => ({
                            ...c,
                            [p.externalId]: {
                              platforms: c[p.externalId]?.platforms ?? [...p.platforms],
                              brandId: e.target.value,
                            },
                          }))
                        }
                        disabled={!entry}
                        className={
                          "rounded-lg border px-2 py-1 text-xs disabled:opacity-50 " +
                          // Unanswered is shown as unanswered. A select that
                          // already reads "Brand A" looks like a decision
                          // nobody made.
                          (entry && !entry.brandId
                            ? "border-danger text-danger"
                            : "border-border")
                        }
                      >
                        <option value="">{t("connect.chooseBrand")}</option>
                        {(brands.data ?? []).map((b) => (
                          <option key={b.id} value={b.id}>
                            {b.name}
                          </option>
                        ))}
                      </select>
                    </li>
                  );
                })}
              </ul>

              {/* The allowance, before the round trip rather than after it.
                  A Facebook page and its Instagram account are two
                  destinations and cost two, which is not obvious from a
                  single row with two ticks in it — and finding out by being
                  bounced back from Facebook with a 409 is a bad way to
                  learn. */}
              <p className="mt-4 text-xs text-muted">
                {t("connect.allowance", {
                  adding: selectedCount,
                  used: connectedCount,
                  limit: limitLabel,
                })}
              </p>
              {overLimit ? (
                <div className="mt-2">
                  <ErrorBox>{t("connect.overLimit", { limit: limitLabel })}</ErrorBox>
                </div>
              ) : null}

              {error ? <div className="mt-3"><ErrorBox>{error}</ErrorBox></div> : null}

              <button
                onClick={() => void submit()}
                disabled={assign.isPending || noBrands || overLimit || missingBrand}
                className="btn-primary mt-4"
              >
                {assign.isPending ? t("connect.connecting") : t("connect.connectSelected")}
              </button>
            </>
          )}
        </section>
      ) : (
        /* ── Entry point ── */
        <section className="card-pad">
          <h2 className="text-sm font-semibold text-ink">{t("connect.addTitle")}</h2>
          {noBrands ? (
            <p className="mt-2 text-sm text-warning">{t("connect.needBrand")}</p>
          ) : (
            <>
              <p className="mt-1 text-xs text-muted">{t("connect.addHint")}</p>

              {/* Chosen here, before leaving, so the brand travels in the
                  OAuth state and comes back on the callback. Skipping this
                  step is what let every page land on the first brand. */}
              {(brands.data?.length ?? 0) > 1 ? (
                <label className="mt-4 block max-w-xs">
                  <span className="label">{t("connect.whichBrand")}</span>
                  <select
                    value={startBrandId}
                    onChange={(e) => setStartBrandId(e.target.value)}
                    className="select"
                  >
                    <option value="">{t("connect.chooseBrand")}</option>
                    {(brands.data ?? []).map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                  <span className="hint">{t("connect.whichBrandHint")}</span>
                </label>
              ) : null}

              {error ? (
                <div className="mt-4">
                  <ErrorBox>{error}</ErrorBox>
                </div>
              ) : null}

              <div className="mt-4 flex flex-wrap gap-3">
                <button
                  onClick={() => startConnect("meta")}
                  disabled={startMeta.isPending || !effectiveStartBrand}
                  className="inline-flex items-center gap-2 rounded-lg bg-[#1877F2] px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
                >
                  <Facebook size={16} />
                  {startMeta.isPending ? t("connect.redirecting") : t("connect.connectMeta")}
                </button>
                <button
                  onClick={() => startConnect("threads")}
                  disabled={startThreads.isPending || !effectiveStartBrand}
                  className="inline-flex items-center gap-2 rounded-lg bg-night-900 px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
                >
                  <AtSign size={16} />
                  {startThreads.isPending
                    ? t("connect.redirecting")
                    : t("connect.connectThreads")}
                </button>
                <span className="inline-flex items-center gap-2 rounded-lg border border-dashed border-border px-4 py-2 text-sm text-muted">
                  <Linkedin size={15} /> {t("connect.linkedinSoon")}
                </span>
                <span className="inline-flex items-center gap-2 rounded-lg border border-dashed border-border px-4 py-2 text-sm text-muted">
                  <Link2 size={15} /> {t("connect.xSoon")}
                </span>
              </div>
            </>
          )}
        </section>
      )}

      {/* ── Already connected ── */}
      <section>
        <h2 className="mb-3 text-sm font-semibold text-ink">{t("connect.connectedTitle")}</h2>
        {notice ? (
          <div className="mb-3">
            <Notice tone="success">{notice}</Notice>
          </div>
        ) : null}
        {accounts.isLoading ? (
          <p className="text-sm text-muted">{t("errors.loading")}</p>
        ) : (accounts.data?.length ?? 0) === 0 ? (
          <p className="rounded-xl border border-dashed border-border bg-white/50 p-6 text-center text-sm text-muted">
            {t("connect.noneConnected")}
          </p>
        ) : (
          <ul className="grid gap-3 sm:grid-cols-2">
            {accounts.data!.map((a) => {
              const Icon = PLATFORM_ICON[a.platform] ?? Plug;
              return (
                <li
                  key={a.id}
                  className="flex min-w-0 items-center gap-3 rounded-xl border border-border bg-white p-4 shadow-sm"
                >
                  <Icon size={18} className="shrink-0 text-primary-600" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">{a.displayName}</span>
                    <span className="block text-xs text-muted">{a.platform}</span>
                    {/* Editable in place: a page on the wrong brand used to
                        mean disconnecting and going back through Facebook to
                        change one field. */}
                    {(brands.data?.length ?? 0) > 1 ? (
                      <select
                        value={a.brand.id}
                        onChange={(e) =>
                          void move
                            .mutateAsync({ accountId: a.id, brandId: e.target.value })
                            .catch((err) => setError(apiErrorMessage(err)))
                        }
                        disabled={move.isPending}
                        aria-label={t("connect.whichBrand")}
                        className="mt-1 w-full max-w-full rounded-lg border border-border px-1.5 py-0.5 text-xs disabled:opacity-50"
                      >
                        {(brands.data ?? []).map((b) => (
                          <option key={b.id} value={b.id}>
                            {b.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className="block text-xs text-muted">{a.brand.name}</span>
                    )}
                  </span>
                  {a.status !== "active" ? (
                    <span className="rounded-full bg-warning/10 px-2 py-0.5 text-[10px] font-semibold text-warning">
                      {t(`connect.status.${a.status}`)}
                    </span>
                  ) : null}
                  <button
                    onClick={() => void onDisconnect(a.id, a.displayName)}
                    disabled={disconnect.isPending}
                    aria-label={t("connect.disconnect")}
                    title={t("connect.disconnect")}
                    className="text-muted hover:text-danger disabled:opacity-50"
                  >
                    <Trash2 size={15} />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
